import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../../utils/api';
import DocumentTemplateSelector from '../../../DocumentTemplateSelector';
import { generateDocumentOutput } from '../../../utils/documentGeneration';
import { getCompanyVariables } from '../../../utils/templateVariables';
import StockStatusTable from '../../../components/StockStatusTable';
import RaisePOModal from '../../../RaisePOModal';

function SalesOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [order, setOrder] = useState({
    customer_id: '',
    site_id: '',
    customer_po_number: '',
    customer_notes: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_dispatch_date: '',
    delivery_method: '',
    delivery_instructions: '',
    collection: false,
    delivery_contact_name: '',
    delivery_contact_phone: '',
    status: 'Ordered',
    notes: ''
  });

  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerSites, setCustomerSites] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLetter, setSelectedLetter] = useState('all');
  const [quickQuantities, setQuickQuantities] = useState({}); // Track qty per product in selector
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [showDocumentTemplateSelector, setShowDocumentTemplateSelector] = useState(false);
  const [showCustomItemForm, setShowCustomItemForm] = useState(false);
  const [customItem, setCustomItem] = useState({ description: '', quantity: 1, unit_price: 0, cost_price: 0 });
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [textTemplates, setTextTemplates] = useState([]);
  const [showCustomerNotesTemplates, setShowCustomerNotesTemplates] = useState(false);
  const [addedNotification, setAddedNotification] = useState(null);
  const [showInternalNotesTemplates, setShowInternalNotesTemplates] = useState(false);
  const [deliverySameAsCustomer, setDeliverySameAsCustomer] = useState(false);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  const [showRaisePOModal, setShowRaisePOModal] = useState(false);
  const [shortageItems, setShortageItems] = useState([]);
  const [newSite, setNewSite] = useState({
    site_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    county: '',
    postcode: ''
  });

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    fetchTemplates();
    fetchStatuses();
    fetchDeliveryMethods();
    fetchTextTemplates();
    if (!isNew) {
      fetchOrder();
    }
  }, [id]);

  const fetchTextTemplates = async () => {
    try {
      const res = await apiFetch('/api/text-templates');
      const data = await res.json();
      setTextTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching text templates:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await apiFetch('/api/templates?area=sales_order');
      const data = await res.json();
      console.log('Templates API response:', data);
      console.log('Templates array:', Array.isArray(data), 'Length:', data?.length);
      setTemplates(Array.isArray(data) ? data : []);
      const defaultTemplate = data.find(t => t.is_default);
      setSelectedTemplate(defaultTemplate || data[0]);
      console.log('Selected template:', defaultTemplate || data[0]);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await apiFetch('/api/customers');
      const data = await res.json();
      // Only show "Supply Only" customers in Sales Orders
      const supplyOnlyCustomers = data.filter(c => c.customer_type === 'supply_only');
      setCustomers(supplyOnlyCustomers);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await apiFetch('/api/products');
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchStatuses = async () => {
    try {
      const res = await apiFetch('/api/sales-order-statuses');
      const data = await res.json();
      setStatuses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching statuses:', err);
    }
  };

  const fetchDeliveryMethods = async () => {
    try {
      const res = await apiFetch('/api/delivery-methods');
      const data = await res.json();
      setDeliveryMethods(Array.isArray(data) ? data : []);

      // Set default delivery method for new orders
      if (isNew && !order.delivery_method) {
        const defaultMethod = data.find(m => m.is_default);
        if (defaultMethod) {
          setOrder(prev => ({ ...prev, delivery_method: defaultMethod.name }));
        }
      }
    } catch (err) {
      console.error('Error fetching delivery methods:', err);
    }
  };

  const fetchOrder = async () => {
    try {
      const res = await apiFetch(`/api/sales-orders/${id}`);
      const data = await res.json();

      // Convert ISO dates to yyyy-MM-dd format for date inputs
      if (data.order_date) {
        data.order_date = data.order_date.split('T')[0];
      }
      if (data.expected_dispatch_date) {
        data.expected_dispatch_date = data.expected_dispatch_date.split('T')[0];
      }

      setOrder(data);
      setItems(data.items || []);
    } catch (err) {
      console.error('Error fetching order:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!order.customer_id) {
      alert('Please select a customer');
      return;
    }

    if (items.length === 0) {
      alert('Please add at least one item');
      return;
    }

    setSaving(true);
    try {
      const url = isNew
        ? '/api/sales-orders'
        : `/api/sales-orders/${id}`;

      const method = isNew ? 'POST' : 'PUT';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...order, items })
      });

      if (!res.ok) throw new Error('Failed to save');

      const data = await res.json();

      if (isNew) {
        navigate(`/sales-orders/${data.id || 1}`);
      } else {
        fetchOrder();
      }

      alert('Sales order saved successfully');
    } catch (err) {
      console.error('Error saving order:', err);
      alert('Failed to save sales order');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedTemplate) {
      alert('Please select a template first');
      return;
    }
    try {
      const templateData = await prepareSalesOrderData(order, items);

      // Use renderTemplateToHTML from pdfGenerator (visual components)
      const { renderTemplateToHTML } = await import('../../../utils/pdfGenerator');
      const html = renderTemplateToHTML(selectedTemplate, templateData);

      setPreviewHtml(html);
      setShowPreview(true);
    } catch (error) {
      console.error('Preview error:', error);
      alert('Error generating preview: ' + error.message);
    }
  };

  const handlePrint = async () => {
    setShowDocumentTemplateSelector(true);
  };

  const handlePDF = async () => {
    setShowDocumentTemplateSelector(true);
  };

  const handleDocumentTemplateGenerate = async (template, action, data, outputWindow = null) => {
    try {
      const templateData = await prepareSalesOrderData(order, items);
      await generateDocumentOutput(template, action, templateData, `SalesOrder-${order.order_number || order.id}`, outputWindow);
      setShowDocumentTemplateSelector(false);
    } catch (error) {
      console.error('Document generation error:', error);
      alert('Error generating document: ' + error.message);
    }
  };

  const handleEmail = async () => {
    alert('Email functionality coming soon!');
  };

  const prepareSalesOrderData = async (orderData, orderItems) => {
    let companySettings = {};
    try {
      const res = await apiFetch('/api/company-settings');
      companySettings = await res.json();
    } catch (err) {
      console.error('Error fetching company settings:', err);
    }

    const companyAddressParts = [
      companySettings.address_line1,
      companySettings.address_line2,
      companySettings.city,
      companySettings.county,
      companySettings.postcode
    ].filter(Boolean);
    const companyAddress = companyAddressParts.join(', ');

    // Fetch full site details if site_id is available
    let deliveryAddress = '';
    let customerAddress = '';

    if (orderData.site_id) {
      try {
        const siteRes = await apiFetch(`/api/sites/${orderData.site_id}`);
        const site = await siteRes.json();

        // Build multi-line delivery address from site fields
        const deliveryParts = [
          site.site_name,
          site.address_line1,
          site.address_line2,
          site.city,
          site.county,
          site.postcode
        ].filter(Boolean);
        deliveryAddress = deliveryParts.join('\n');
      } catch (err) {
        console.error('Error fetching site:', err);
        deliveryAddress = (orderData.delivery_address || '').replace(/,\s*/g, '\n');
      }
    } else {
      deliveryAddress = (orderData.delivery_address || '').replace(/,\s*/g, '\n');
    }

    // Fetch customer address if customer_id is available
    if (orderData.customer_id) {
      try {
        const custRes = await apiFetch(`/api/customers/${orderData.customer_id}`);
        const customer = await custRes.json();
        customerAddress = customer.address || '';
      } catch (err) {
        console.error('Error fetching customer:', err);
      }
    }

    const subtotal = orderItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const vatAmount = subtotal * 0.20;
    const total = subtotal + vatAmount;

    return {
      customer_name: orderData.customer_name || '',
      customer_address: customerAddress,
      customer_po: orderData.customer_po_number || '',
      customer_po_number: orderData.customer_po_number || '', // legacy
      order_number: orderData.order_number || '',
      order_date: orderData.order_date ? new Date(orderData.order_date).toLocaleDateString() : '',
      expected_dispatch: orderData.expected_dispatch_date ? new Date(orderData.expected_dispatch_date).toLocaleDateString() : '',
      expected_dispatch_date: orderData.expected_dispatch_date ? new Date(orderData.expected_dispatch_date).toLocaleDateString() : '', // legacy
      delivery_date: new Date().toLocaleDateString(), // Today's date for delivery note
      delivery_address: deliveryAddress,
      delivery_address_short: deliveryAddress.split('\n')[0] || '', // First line only
      delivery_method: orderData.delivery_method || '',
      delivery_contact: orderData.delivery_contact_name || '',
      delivery_contact_name: orderData.delivery_contact_name || '', // legacy
      delivery_phone: orderData.delivery_contact_phone || '',
      delivery_contact_phone: orderData.delivery_contact_phone || '', // legacy
      delivery_instructions: orderData.delivery_instructions || '',
      customer_instructions: orderData.customer_notes || '',
      customer_notes: orderData.customer_notes || '', // legacy
      status: orderData.status || '',
      items: orderItems.map(item => ({
        product_code: item.product_code || '',
        description: item.description || '',
        quantity: parseFloat(item.quantity) || 0,
        unit_price: (parseFloat(item.unit_price) || 0).toFixed(2),
        total: ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)
      })),
      subtotal: subtotal.toFixed(2),
      delivery_charge: orderData.collection ? '0.00' : (() => {
        if (!orderData.delivery_method) return '0.00';
        const method = deliveryMethods.find(m => m.name === orderData.delivery_method);
        return parseFloat(method?.charge || 0).toFixed(2);
      })(),
      vat: vatAmount.toFixed(2),
      vat_amount: vatAmount.toFixed(2), // legacy
      total: total.toFixed(2),
      notes: orderData.notes || '',

      // Company fields (consistent across all templates)
      ...getCompanyVariables(companySettings)
    };
  };

  const addProduct = (product, quantityOverride = null) => {
    const quantity = quantityOverride || quickQuantities[product.id] || 1;

    const newItem = {
      product_id: product.id,
      product_code: product.product_code || '',
      description: product.product_name,
      quantity: quantity,
      unit_price: parseFloat(product.supply_price) || parseFloat(product.price) || 0, // Use Tier 1 (supply price)
      cost_price: parseFloat(product.cost_price) || 0,
      is_custom: false,
      stock_available: product.quantity || 0
    };
    setItems([...items, newItem]);

    // Clear quick quantity for this product
    const newQuickQtys = { ...quickQuantities };
    delete newQuickQtys[product.id];
    setQuickQuantities(newQuickQtys);
  };

  const handleProductClick = (product) => {
    const stockAvailable = parseFloat(product.quantity) || 0;
    const requestedQty = quickQuantities[product.id] || 1;

    if (stockAvailable <= 0) {
      // Out of stock - show options
      if (window.confirm(`${product.product_name} is OUT OF STOCK.\n\nAdd anyway as back-order?`)) {
        addProduct(product, requestedQty);
        // Show notification
        setAddedNotification(`Added ${requestedQty}x ${product.product_name} (BACK-ORDER)`);
        setTimeout(() => setAddedNotification(null), 2000);
        // Keep modal open for adding more products
        setProductSearch('');
        setQuickQuantities(prev => ({ ...prev, [product.id]: 1 })); // Reset qty for this product
      }
    } else if (requestedQty > stockAvailable) {
      // Insufficient stock - show warning
      if (window.confirm(`Only ${stockAvailable} in stock. You requested ${requestedQty}.\n\nAdd ${stockAvailable} now and back-order the rest?`)) {
        addProduct(product, requestedQty);
        // Show notification
        setAddedNotification(`Added ${requestedQty}x ${product.product_name} (${stockAvailable} in stock, rest back-ordered)`);
        setTimeout(() => setAddedNotification(null), 3000);
        // Keep modal open for adding more products
        setProductSearch('');
        setQuickQuantities(prev => ({ ...prev, [product.id]: 1 })); // Reset qty for this product
      }
    } else {
      // Stock available - add normally
      addProduct(product, requestedQty);
      // Show notification
      setAddedNotification(`Added ${requestedQty}x ${product.product_name}`);
      setTimeout(() => setAddedNotification(null), 2000);
      // Keep modal open for adding more products
      setProductSearch('');
      setQuickQuantities(prev => ({ ...prev, [product.id]: 1 })); // Reset qty for this product
    }
  };

  const addCustomItem = () => {
    if (!customItem.description.trim()) {
      alert('Please enter a description');
      return;
    }

    const newItem = {
      product_id: null,
      product_code: '',
      description: customItem.description,
      quantity: parseFloat(customItem.quantity) || 1,
      unit_price: parseFloat(customItem.unit_price) || 0,
      cost_price: parseFloat(customItem.cost_price) || 0,
      is_custom: true
    };

    setItems([...items, newItem]);
    setShowCustomItemForm(false);
    setCustomItem({ description: '', quantity: 1, unit_price: 0, cost_price: 0 });
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const itemsSubtotal = items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + (qty * price);
    }, 0);

    const costTotal = items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const cost = parseFloat(item.cost_price) || 0;
      return sum + (qty * cost);
    }, 0);

    // Get delivery charge
    const deliveryCharge = order.collection ? 0 : (() => {
      if (!order.delivery_method) return 0;
      const method = deliveryMethods.find(m => m.name === order.delivery_method);
      return parseFloat(method?.charge || 0);
    })();

    const subtotal = itemsSubtotal + deliveryCharge;
    const vatAmount = subtotal * 0.20;
    const total = subtotal + vatAmount;
    const profit = subtotal - costTotal; // Profit = Revenue (excl VAT) - Cost
    const profitMargin = subtotal > 0 ? (profit / subtotal) * 100 : 0; // Margin based on revenue excl VAT

    return { itemsSubtotal, deliveryCharge, subtotal, vatAmount, total, costTotal, profit, profitMargin };
  };

  const totals = calculateTotals();

  const handleCustomerChange = async (customerId) => {
    setOrder(prev => ({ ...prev, customer_id: customerId, site_id: '' }));

    // Fetch customer's sites
    if (customerId) {
      try {
        const res = await apiFetch(`/api/sites?customer_id=${customerId}`);
        const data = await res.json();
        setCustomerSites(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching sites:', err);
        setCustomerSites([]);
      }
    } else {
      setCustomerSites([]);
    }
  };

  const handleSiteSelect = (siteId) => {
    setOrder(prev => ({ ...prev, site_id: siteId }));
  };

  const handleAddSite = async () => {
    if (!newSite.site_name || !newSite.address_line1) {
      alert('Please enter site name and address');
      return;
    }

    try {
      const res = await apiFetch('/api/sites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newSite,
          customer_id: order.customer_id
        })
      });

      const savedSite = await res.json();

      // Refresh sites list
      const sitesRes = await apiFetch(`/api/sites?customer_id=${order.customer_id}`);
      const sites = await sitesRes.json();
      setCustomerSites(Array.isArray(sites) ? sites : []);

      // Select the newly added site
      setOrder(prev => ({ ...prev, site_id: savedSite.id }));

      // Reset and close modal
      setNewSite({
        site_name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        county: '',
        postcode: ''
      });
      setShowAddSiteModal(false);

    } catch (err) {
      console.error('Error adding site:', err);
      alert('Failed to add site');
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/sales-orders')} className="text-white hover:text-blue-100 transition text-lg font-semibold">
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">
                {isNew ? 'New Sales Order' : `Sales Order ${order.order_number || ''}`}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {!isNew && (
                <>
                  <div className="relative">
                    <button
                      onClick={() => setShowTemplateMenu(!showTemplateMenu)}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm flex items-center gap-1"
                    >
                      📋 {selectedTemplate ? selectedTemplate.name : 'Template'} ▼
                    </button>
                    {showTemplateMenu && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowTemplateMenu(false)}></div>
                        <div className="absolute right-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                          {templates.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-500">No templates found</div>
                          ) : (
                            templates.map(template => (
                              <button
                                key={template.id}
                                onClick={() => {
                                  setSelectedTemplate(template);
                                  setShowTemplateMenu(false);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 transition first:rounded-t-lg last:rounded-b-lg ${
                                  selectedTemplate?.id === template.id ? 'bg-blue-50' : ''
                                }`}
                              >
                                <div className="font-medium text-gray-900">{template.name}</div>
                                {template.is_default && <div className="text-xs text-blue-600">Default</div>}
                              </button>
                            ))
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={handlePreview}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                  >
                    👁️ Preview
                  </button>
                  <button
                    onClick={handlePrint}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                  >
                    🖨️ Print
                  </button>
                  <button
                    onClick={handlePDF}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                  >
                    📄 PDF
                  </button>
                  <button
                    onClick={handleEmail}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                  >
                    ✉️ Email
                  </button>
                </>
              )}
              {!isNew && (
                <button
                  onClick={() => navigate(`/purchase-orders/new?sales_order_id=${id}&sales_order_number=${order.order_number}&site_id=${order.site_id}`)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition text-sm shadow-sm"
                >
                  📦 Create PO
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                {saving ? 'Saving...' : '💾 Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">

            {/* Section 1: Order Details */}
            <div className="bg-blue-50 rounded-lg shadow p-6 border-l-4 border-blue-400">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                Order Details
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                  <select
                    value={order.customer_id}
                    onChange={(e) => handleCustomerChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select customer...</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name}</option>
                    ))}
                  </select>
                  {order.customer_id && (() => {
                    const customer = customers.find(c => c.id === parseInt(order.customer_id));
                    if (!customer) return null;
                    const addressParts = [
                      customer.address_line1,
                      customer.address_line2,
                      customer.city,
                      customer.county,
                      customer.postcode
                    ].filter(Boolean);
                    if (addressParts.length === 0) return null;
                    return (
                      <div className="mt-2 p-3 bg-white border border-gray-300 rounded text-sm text-gray-700 whitespace-pre-line">
                        {addressParts.join('\n')}
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700">Delivery Address *</label>
                    <label className="flex items-center text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={deliverySameAsCustomer}
                        onChange={(e) => {
                          setDeliverySameAsCustomer(e.target.checked);
                          if (e.target.checked) {
                            setOrder(prev => ({ ...prev, site_id: '' }));
                          }
                        }}
                        disabled={!order.customer_id}
                        className="mr-1"
                      />
                      Same as customer
                    </label>
                  </div>

                  {!deliverySameAsCustomer && (
                    <>
                      <div className="flex gap-2">
                        <select
                          value={order.site_id}
                          onChange={(e) => handleSiteSelect(e.target.value)}
                          disabled={!order.customer_id}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                        >
                          <option value="">Select delivery site...</option>
                          {customerSites.map(site => (
                            <option key={site.id} value={site.id}>
                              {site.site_name} - {site.site_address || site.address}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowAddSiteModal(true)}
                          disabled={!order.customer_id}
                          className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          title="Add new delivery address"
                        >
                          + Add
                        </button>
                      </div>
                      {order.customer_id && customerSites.length === 0 && (
                        <p className="text-xs text-gray-500 mt-1">No sites found - click "+ Add" to create one</p>
                      )}
                      {order.site_id && customerSites.find(s => s.id === parseInt(order.site_id)) && (
                        <div className="mt-2 p-3 bg-white border border-gray-300 rounded text-sm text-gray-700 whitespace-pre-line">
                          {(() => {
                            const site = customerSites.find(s => s.id === parseInt(order.site_id));
                            const addressParts = [
                              site.site_name,
                              site.address_line1,
                              site.address_line2,
                              site.city,
                              site.county,
                              site.postcode
                            ].filter(Boolean);
                            return addressParts.join('\n');
                          })()}
                        </div>
                      )}
                    </>
                  )}

                  {deliverySameAsCustomer && order.customer_id && (() => {
                    const customer = customers.find(c => c.id === parseInt(order.customer_id));
                    if (!customer) return null;
                    const addressParts = [
                      customer.address_line1,
                      customer.address_line2,
                      customer.city,
                      customer.county,
                      customer.postcode
                    ].filter(Boolean);
                    if (addressParts.length === 0) return <p className="text-xs text-gray-500 mt-1">No customer address available</p>;
                    return (
                      <div className="mt-2 p-3 bg-white border border-gray-300 rounded text-sm text-gray-700 whitespace-pre-line">
                        {addressParts.join('\n')}
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order Date</label>
                  <input
                    type="date"
                    value={order.order_date || ''}
                    onChange={(e) => setOrder({...order, order_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected Dispatch Date</label>
                  <input
                    type="date"
                    value={order.expected_dispatch_date || ''}
                    onChange={(e) => setOrder({...order, expected_dispatch_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer PO Number</label>
                  <input
                    type="text"
                    value={order.customer_po_number || ''}
                    onChange={(e) => setOrder({...order, customer_po_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                    placeholder="Customer's PO number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={order.status || 'Ordered'}
                    onChange={(e) => setOrder({...order, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                  >
                    {statuses.length === 0 ? (
                      <option>Ordered</option>
                    ) : (
                      statuses.map(status => (
                        <option key={status.id} value={status.name}>{status.name}</option>
                      ))
                    )}
                  </select>
                </div>
              </div>
            </div>

            {/* Section 2: Customer Instructions */}
            <div className="bg-blue-50 rounded-lg shadow p-6 border-l-4 border-blue-400">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                  Customer Instructions
                </h2>
                <button
                  onClick={() => setShowCustomerNotesTemplates(true)}
                  className="px-3 py-1.5 bg-gray-300 hover:bg-gray-400 text-black rounded text-sm font-medium"
                >
                  📋 Templates
                </button>
              </div>
              <textarea
                value={order.customer_notes || ''}
                onChange={(e) => setOrder({...order, customer_notes: e.target.value})}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                placeholder="Customer's instructions, requirements, or special notes..."
              />
            </div>

            {/* Section 3: Line Items */}
            <div className="bg-blue-50 rounded-lg shadow p-6 border-l-4 border-blue-400">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                  Line Items
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowProductSelector(true)}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
                  >
                    + Add Product
                  </button>
                  <button
                    onClick={() => setShowCustomItemForm(true)}
                    className="px-3 py-1.5 bg-gray-300 hover:bg-gray-400 text-black rounded text-sm font-medium"
                  >
                    + Custom Item
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32">Part Number</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-20">Qty</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-24">Cost</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-24">Sell Price</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-24">Profit</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-28">Total</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-3 py-8 text-center text-gray-500 text-sm">
                          No items yet. Click "Add Product" or "Custom Item" to get started.
                        </td>
                      </tr>
                    ) : (
                      items.map((item, index) => {
                        const qty = parseFloat(item.quantity) || 0;
                        const cost = parseFloat(item.cost_price) || 0;
                        const price = parseFloat(item.unit_price) || 0;
                        const lineProfit = (qty * price) - (qty * cost);
                        const lineTotal = qty * price;

                        return (
                          <tr key={index} className={item.is_custom ? 'bg-yellow-50' : ''}>
                            <td className="px-3 py-2">
                              {item.is_custom ? (
                                <span className="text-xs text-gray-500 italic">Custom</span>
                              ) : (
                                <span className="text-xs font-mono text-gray-700">{item.product_code || '-'}</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => updateItem(index, 'description', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                step="0.01"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                value={item.cost_price}
                                onChange={(e) => updateItem(index, 'cost_price', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                step="0.01"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                value={item.unit_price}
                                onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                step="0.01"
                              />
                            </td>
                            <td className="px-3 py-2 text-right">
                              <span className={`text-sm font-medium ${lineProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                £{lineProfit.toFixed(2)}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right text-sm font-medium">
                              £{lineTotal.toFixed(2)}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => removeItem(index)}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Section 4: Delivery Details */}
            <div className="bg-blue-50 rounded-lg shadow p-6 border-l-4 border-blue-400">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">4</span>
                Delivery Details
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                    <input
                      type="checkbox"
                      checked={order.collection || false}
                      onChange={(e) => setOrder({...order, collection: e.target.checked})}
                      className="rounded"
                    />
                    <span>Customer Collection (no delivery charge)</span>
                  </label>
                </div>

                {!order.collection && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Method</label>
                      <select
                        value={order.delivery_method || ''}
                        onChange={(e) => setOrder({...order, delivery_method: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">Select delivery method...</option>
                        {deliveryMethods.map(method => (
                          <option key={method.id} value={method.name}>
                            {method.name} - £{parseFloat(method.charge || 0).toFixed(2)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Contact Name</label>
                      <input
                        type="text"
                        value={order.delivery_contact_name || ''}
                        onChange={(e) => setOrder({...order, delivery_contact_name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                        placeholder="Contact person at delivery site"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Contact Phone</label>
                      <input
                        type="tel"
                        value={order.delivery_contact_phone || ''}
                        onChange={(e) => setOrder({...order, delivery_contact_phone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                        placeholder="Contact phone number"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Instructions</label>
                      <textarea
                        value={order.delivery_instructions || ''}
                        onChange={(e) => setOrder({...order, delivery_instructions: e.target.value})}
                        rows="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                        placeholder="Special delivery instructions, access codes, parking info, etc."
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="bg-blue-50 rounded-lg shadow p-6 border-l-4 border-blue-400">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Internal Notes</label>
                <button
                  onClick={() => setShowInternalNotesTemplates(true)}
                  className="px-3 py-1.5 bg-gray-300 hover:bg-gray-400 text-black rounded text-sm font-medium"
                >
                  📋 Templates
                </button>
              </div>
              <textarea
                value={order.notes || ''}
                onChange={(e) => setOrder({...order, notes: e.target.value})}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                placeholder="Internal notes (not shown to customer)"
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Totals */}
            <div className="bg-blue-50 rounded-lg shadow p-6 border-l-4 border-blue-400">
              <h3 className="font-semibold text-gray-900 mb-4">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Items Subtotal</span>
                  <span className="font-medium">£{totals.itemsSubtotal.toFixed(2)}</span>
                </div>
                {!order.collection && totals.deliveryCharge > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery</span>
                    <span className="font-medium">£{totals.deliveryCharge.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">£{totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">VAT (20%)</span>
                  <span className="font-medium">£{totals.vatAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t-2 border-gray-300">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="font-bold text-lg text-blue-600">£{totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Profit Summary */}
            <div className="bg-green-50 rounded-lg shadow p-6 border-l-4 border-green-400">
              <h3 className="font-semibold text-gray-900 mb-4">Profit Analysis</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Revenue (excl VAT)</span>
                  <span className="font-medium">£{totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Cost</span>
                  <span className="font-medium">£{totals.costTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t-2 border-gray-300">
                  <span className="font-semibold text-gray-900">Profit</span>
                  <span className={`font-bold text-lg ${totals.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    £{totals.profit.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Margin</span>
                  <span className={`font-medium ${totals.profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {totals.profitMargin.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Selector Modal */}
      {showProductSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col relative">
            {/* Added notification */}
            {addedNotification && (
              <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-10 animate-fade-in max-w-md">
                ✓ {addedNotification}
              </div>
            )}

            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Select Products</h3>
                <button
                  onClick={() => {
                    setShowProductSelector(false);
                    setProductSearch('');
                    setSelectedCategory('all');
                    setSelectedLetter('all');
                    setQuickQuantities({});
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-3">
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Search by PART NUMBER, name, or description..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />

                {/* A-Z Letter Filter */}
                <div className="space-y-2">
                  <div className="flex gap-1 flex-wrap">
                    <button
                      onClick={() => { setSelectedLetter('all'); setSelectedCategory('all'); }}
                      className={`px-2 py-1 rounded text-xs font-medium transition ${
                        selectedLetter === 'all'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      All
                    </button>
                    {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => (
                      <button
                        key={letter}
                        onClick={() => { setSelectedLetter(letter); setSelectedCategory('all'); }}
                        className={`px-2 py-1 rounded text-xs font-medium transition ${
                          selectedLetter === letter
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {letter}
                      </button>
                    ))}
                  </div>

                  {/* Category Filter (filtered by letter) */}
                  {selectedLetter !== 'all' && (
                    <div className="flex gap-2 flex-wrap">
                      <span className="text-xs text-gray-600 py-1">Categories {selectedLetter}:</span>
                      {Array.from(new Set(
                        products
                          .filter(p => p.product_category)
                          .map(p => p.product_category)
                          .filter(cat => cat.toUpperCase().startsWith(selectedLetter))
                      )).sort().map(category => (
                        <button
                          key={category}
                          onClick={() => setSelectedCategory(category)}
                          className={`px-3 py-1 rounded text-xs font-medium transition ${
                            selectedCategory === category
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {category}
                        </button>
                      ))}
                      {Array.from(new Set(
                        products
                          .filter(p => p.product_category)
                          .map(p => p.product_category)
                          .filter(cat => cat.toUpperCase().startsWith(selectedLetter))
                      )).length === 0 && (
                        <span className="text-xs text-gray-400 py-1">No categories starting with {selectedLetter}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-2">
                {products
                  .filter(p => p.is_active)
                  .filter(p => p.product_category !== 'Labour') // Hide Labour from Sales Orders
                  .filter(p => {
                    if (selectedCategory !== 'all') return p.product_category === selectedCategory;
                    if (selectedLetter === 'all') return true;
                    return p.product_category && p.product_category.toUpperCase().startsWith(selectedLetter);
                  })
                  .filter(p => {
                    if (!productSearch) return true;
                    const search = productSearch.toLowerCase();
                    return (
                      p.product_code?.toLowerCase().includes(search) ||
                      p.product_name?.toLowerCase().includes(search) ||
                      p.product_description?.toLowerCase().includes(search) ||
                      p.product_category?.toLowerCase().includes(search)
                    );
                  })
                  .sort((a, b) => {
                    // Prioritize product_code matches
                    if (!productSearch) return 0;
                    const search = productSearch.toLowerCase();
                    const aCodeMatch = a.product_code?.toLowerCase().includes(search);
                    const bCodeMatch = b.product_code?.toLowerCase().includes(search);
                    if (aCodeMatch && !bCodeMatch) return -1;
                    if (!aCodeMatch && bCodeMatch) return 1;
                    return 0;
                  })
                  .map(product => {
                    const stockAvailable = parseFloat(product.quantity) || 0;
                    const isOutOfStock = stockAvailable <= 0;
                    const isLowStock = stockAvailable > 0 && stockAvailable <= 5;

                    return (
                      <div
                        key={product.id}
                        className={`p-4 border rounded transition ${
                          isOutOfStock
                            ? 'border-red-300 bg-red-50'
                            : isLowStock
                            ? 'border-yellow-300 bg-yellow-50'
                            : 'border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-3 mb-1">
                              {product.product_code && (
                                <div className="text-base font-bold text-blue-600 font-mono bg-blue-50 px-2 py-0.5 rounded">
                                  {product.product_code}
                                </div>
                              )}
                              <div className="font-semibold text-gray-900">{product.product_name}</div>
                            </div>
                            {product.product_description && (
                              <div className="text-sm text-gray-600 mt-1">{product.product_description}</div>
                            )}
                            <div className="flex items-center gap-3 mt-2">
                              {product.product_category && (
                                <span className="text-xs text-gray-500">Category: {product.product_category}</span>
                              )}
                              <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                isOutOfStock
                                  ? 'bg-red-100 text-red-700'
                                  : isLowStock
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {isOutOfStock ? '⚠️ OUT OF STOCK' : `✓ ${stockAvailable} in stock`}
                              </span>
                            </div>
                          </div>

                          <div className="text-right flex-shrink-0">
                            <div className="font-bold text-lg text-green-600 mb-2">£{parseFloat(product.supply_price || product.price || 0).toFixed(2)}</div>

                            <div className="flex items-center gap-2 mb-2">
                              <label className="text-xs text-gray-600">Qty:</label>
                              <input
                                type="number"
                                min="1"
                                value={quickQuantities[product.id] || 1}
                                onChange={(e) => setQuickQuantities({
                                  ...quickQuantities,
                                  [product.id]: parseInt(e.target.value) || 1
                                })}
                                onClick={(e) => e.stopPropagation()}
                                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                              />
                            </div>

                            <button
                              onClick={() => handleProductClick(product)}
                              className={`px-4 py-2 rounded font-medium text-sm transition ${
                                isOutOfStock
                                  ? 'bg-red-600 hover:bg-red-700 text-white'
                                  : 'bg-blue-600 hover:bg-blue-700 text-white'
                              }`}
                            >
                              {isOutOfStock ? 'Back-Order' : 'Add to Order'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                {products.filter(p => p.is_active).filter(p => {
                  if (!productSearch) return true;
                  const search = productSearch.toLowerCase();
                  return (
                    p.product_code?.toLowerCase().includes(search) ||
                    p.product_name?.toLowerCase().includes(search) ||
                    p.product_description?.toLowerCase().includes(search) ||
                    p.product_category?.toLowerCase().includes(search)
                  );
                }).length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    {productSearch ? (
                      <>
                        <p className="text-lg">No products match "{productSearch}"</p>
                        <p className="text-sm mt-2">Try searching by part number or use "Custom Item" for one-off parts</p>
                      </>
                    ) : (
                      <>
                        <p>No products available</p>
                        <p className="text-sm mt-2">Add products in the Stock → Products section</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <DocumentTemplateSelector
        isOpen={showDocumentTemplateSelector}
        onClose={() => setShowDocumentTemplateSelector(false)}
        documentType="sales_order"
        placementKey="sales_order.print_pdf"
        data={order}
        onGenerate={handleDocumentTemplateGenerate}
      />

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Sales Order Preview</h3>
              <div className="flex gap-2">
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
                >
                  🖨️ Print
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded font-medium"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div
                className="bg-white border border-gray-300 p-8"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Customer Notes Templates Modal */}
      {showCustomerNotesTemplates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Customer Instructions Templates</h3>
                <button
                  onClick={() => setShowCustomerNotesTemplates(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-2">
                {textTemplates
                  .filter(t => t.category === 'sales_order' || t.category === 'customer_instructions')
                  .map(template => (
                    <button
                      key={template.id}
                      onClick={() => {
                        setOrder({ ...order, customer_notes: template.content });
                        setShowCustomerNotesTemplates(false);
                      }}
                      className="w-full text-left p-4 border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-300 transition"
                    >
                      <div className="font-semibold text-gray-900">{template.name}</div>
                      <div className="text-sm text-gray-600 mt-1 line-clamp-2">{template.content}</div>
                    </button>
                  ))}

                {textTemplates.filter(t => t.category === 'sales_order' || t.category === 'customer_instructions').length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <p>No customer instruction templates found</p>
                    <p className="text-sm mt-2">Create templates in Settings → Note Templates</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Internal Notes Templates Modal */}
      {showInternalNotesTemplates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Internal Notes Templates</h3>
                <button
                  onClick={() => setShowInternalNotesTemplates(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-2">
                {textTemplates
                  .filter(t => t.category === 'internal' || t.category === 'sales_order_internal')
                  .map(template => (
                    <button
                      key={template.id}
                      onClick={() => {
                        setOrder({ ...order, notes: template.content });
                        setShowInternalNotesTemplates(false);
                      }}
                      className="w-full text-left p-4 border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-300 transition"
                    >
                      <div className="font-semibold text-gray-900">{template.name}</div>
                      <div className="text-sm text-gray-600 mt-1 line-clamp-2">{template.content}</div>
                    </button>
                  ))}

                {textTemplates.filter(t => t.category === 'internal' || t.category === 'sales_order_internal').length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <p>No internal notes templates found</p>
                    <p className="text-sm mt-2">Create templates in Settings → Note Templates</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Item Modal */}
      {showCustomItemForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Add Custom Item</h3>
                <button
                  onClick={() => {
                    setShowCustomItemForm(false);
                    setCustomItem({ description: '', quantity: 1, unit_price: 0, cost_price: 0 });
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input
                  type="text"
                  value={customItem.description}
                  onChange={(e) => setCustomItem({ ...customItem, description: e.target.value })}
                  placeholder="One-off part or custom item description"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    value={customItem.quantity}
                    onChange={(e) => setCustomItem({ ...customItem, quantity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price (£)</label>
                  <input
                    type="number"
                    value={customItem.cost_price}
                    onChange={(e) => setCustomItem({ ...customItem, cost_price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sell Price (£)</label>
                  <input
                    type="number"
                    value={customItem.unit_price}
                    onChange={(e) => setCustomItem({ ...customItem, unit_price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                    step="0.01"
                  />
                </div>
              </div>

              {parseFloat(customItem.unit_price) > 0 && parseFloat(customItem.cost_price) > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Line Profit:</span>
                    <span className={`font-bold ${
                      (parseFloat(customItem.unit_price) - parseFloat(customItem.cost_price)) >= 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}>
                      £{((parseFloat(customItem.unit_price) - parseFloat(customItem.cost_price)) * parseFloat(customItem.quantity || 1)).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={addCustomItem}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
                >
                  Add Item
                </button>
                <button
                  onClick={() => {
                    setShowCustomItemForm(false);
                    setCustomItem({ description: '', quantity: 1, unit_price: 0, cost_price: 0 });
                  }}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Status Section */}
      {!isNew && id && (
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Stock Availability</h3>
            <StockStatusTable
              type="sales-order"
              id={id}
              onRaisePO={(items) => {
                setShortageItems(items);
                setShowRaisePOModal(true);
              }}
            />
          </div>
        </div>
      )}

      {/* Add Site Modal */}
      {showAddSiteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Delivery Address</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site Name *</label>
                <input
                  type="text"
                  value={newSite.site_name}
                  onChange={(e) => setNewSite({...newSite, site_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g., Main Office, Warehouse A"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1 *</label>
                <input
                  type="text"
                  value={newSite.address_line1}
                  onChange={(e) => setNewSite({...newSite, address_line1: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                  placeholder="Street address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                <input
                  type="text"
                  value={newSite.address_line2}
                  onChange={(e) => setNewSite({...newSite, address_line2: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                  placeholder="Building, floor, unit"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={newSite.city}
                    onChange={(e) => setNewSite({...newSite, city: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
                  <input
                    type="text"
                    value={newSite.county}
                    onChange={(e) => setNewSite({...newSite, county: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                <input
                  type="text"
                  value={newSite.postcode}
                  onChange={(e) => setNewSite({...newSite, postcode: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g., CO1 1AA"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowAddSiteModal(false);
                  setNewSite({
                    site_name: '',
                    address_line1: '',
                    address_line2: '',
                    city: '',
                    county: '',
                    postcode: ''
                  });
                }}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSite}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium"
              >
                Save & Select
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Raise PO Modal */}
      {showRaisePOModal && (
        <RaisePOModal
          shortageItems={shortageItems}
          linkedTo="sales-order"
          linkedId={id}
          linkedNumber={order?.order_number}
          onClose={() => setShowRaisePOModal(false)}
          onSuccess={(result) => {
            setShowRaisePOModal(false);
            alert(`Successfully created ${result.count} purchase order(s)!`);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}

export default SalesOrderDetail;
